import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowRight, Loader2, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '@/lib/supabase';

interface LeadFormProps {
  isOpen: boolean;
  onClose: () => void;
  source?: string;
}

// Function to get URL parameters
const getURLParameters = () => {
  if (typeof window === 'undefined') return {};
  
  const urlParams = new URLSearchParams(window.location.search);
  return {
    utm_source: urlParams.get('utm_source') || '',
    utm_medium: urlParams.get('utm_medium') || '',
    utm_campaign: urlParams.get('utm_campaign') || '',
    utm_content: urlParams.get('utm_content') || '',
    utm_term: urlParams.get('utm_term') || '',
    gclid: urlParams.get('gclid') || '',
    fbclid: urlParams.get('fbclid') || '',
    twclid: urlParams.get('twclid') || '',
  };
};

export function LeadForm({ isOpen, onClose, source }: LeadFormProps) {
  const router = useRouter();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  // Handle browser back button
  useEffect(() => {
    if (!isOpen) return;

    // Add a new entry to the history stack
    const state = { modal: true };
    const currentPath = window.location.pathname;
    window.history.pushState(state, '', currentPath);

    const handlePopState = () => {
      onClose();
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isOpen, onClose]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Get the selected option's text content
      const messageSelect = document.getElementById('message') as HTMLSelectElement;
      const selectedOption = messageSelect.options[messageSelect.selectedIndex].text;

      const submissionData = {
        ...formData,
        message: selectedOption
      };

      // Get tracking parameters
      const trackingParams = getURLParameters();

      // Store form data in localStorage
      localStorage.setItem('leadFormData', JSON.stringify(submissionData));
      
      // Store in Supabase with enhanced tracking
      const { error } = await supabase
        .from('leads')
        .insert([
          {
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            message: selectedOption,
            budget: 'not-specified',
            created_at: new Date().toISOString(),
            source: source || trackingParams.utm_source || 'direct',
            utm_medium: trackingParams.utm_medium,
            utm_campaign: trackingParams.utm_campaign,
            utm_content: trackingParams.utm_content,
            utm_term: trackingParams.utm_term,
            gclid: trackingParams.gclid,
            fbclid: trackingParams.fbclid,
            twclid: trackingParams.twclid,
            referrer: typeof window !== 'undefined' ? document.referrer : '',
            landing_page: typeof window !== 'undefined' ? window.location.pathname : '',
          }
        ]);

      if (error) {
        throw error;
      }

      // Track conversion in Google Ads if gclid is present
      if (trackingParams.gclid && typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', {
          'send_to': 'AW-16956898685/xcVjCISL97AaEP361pU_',
          'value': 1.0,
          'currency': 'USD',
          'transaction_id': '',
        });
      }

      // Close modal and redirect
      onClose();
      router.push('/thank-you');
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Portal container for modal */}
          <div className="fixed inset-0 z-[100] overflow-y-auto">
            <div className="min-h-screen px-4 text-center">
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/60 backdrop-blur-sm"
                onClick={handleBackdropClick}
              />

              {/* Modal alignment helper */}
              <span className="inline-block h-screen align-middle" aria-hidden="true">&#8203;</span>

              {/* Modal */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="relative inline-block w-full sm:w-[28rem] my-8 text-left align-middle transform bg-[#1a1f2e] rounded-xl shadow-xl"
              >
                {/* Header */}
                <div className="sticky top-0 flex items-center justify-between p-4 sm:p-6 border-b border-gray-700 bg-[#1a1f2e] rounded-t-xl">
                  <h3 className="text-xl font-semibold text-white">Contact Us</h3>
                  <button
                    type="button"
                    onClick={onClose}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                {/* Form */}
                <div className="max-h-[calc(90vh-8rem)] overflow-y-auto">
                  <form onSubmit={handleSubmit} className="p-4 sm:p-6">
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="name" className="text-white text-sm font-medium">Full Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          className="mt-1 bg-white/10 border-gray-700 text-white focus:ring-emerald-500 focus:border-emerald-500"
                          placeholder="John Doe"
                          disabled={isSubmitting}
                        />
                      </div>

                      <div>
                        <Label htmlFor="email" className="text-white text-sm font-medium">Email Address *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          className="mt-1 bg-white/10 border-gray-700 text-white focus:ring-emerald-500 focus:border-emerald-500"
                          placeholder="john@example.com"
                          disabled={isSubmitting}
                        />
                      </div>

                      <div>
                        <Label htmlFor="phone" className="text-white text-sm font-medium">Phone Number *</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          required
                          value={formData.phone}
                          onChange={handleChange}
                          className="mt-1 bg-white/10 border-gray-700 text-white focus:ring-emerald-500 focus:border-emerald-500"
                          placeholder="+91 98765 43210"
                          disabled={isSubmitting}
                        />
                      </div>

                      <div>
                        <Label htmlFor="message" className="text-white text-sm font-medium">Project Status *</Label>
                        <select
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          className="mt-1 w-full rounded-md border border-gray-700 bg-white/10 px-3 py-2 text-white focus:ring-emerald-500 focus:border-emerald-500"
                          disabled={isSubmitting}
                          required
                        >
                          <option value="">Select your project status</option>
                          <option value="need-help">I&apos;ve an idea, need help in building</option>
                          <option value="in-progress">Website and app development</option>
                          <option value="scaling">Scaling existing business</option>
                          <option value="custom">Need custom solution</option>
                        </select>
                      </div>
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold h-11 relative overflow-hidden group mt-6"
                    >
                      <span className="absolute inset-0 w-0 bg-white/20 transition-all duration-500 ease-out group-hover:w-full"></span>
                      <span className="relative flex items-center justify-center gap-2">
                        {isSubmitting ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span className="text-sm">Submitting...</span>
                          </>
                        ) : (
                          <>
                            <span className="text-sm">Send Message</span>
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                          </>
                        )}
                      </span>
                    </Button>

                    <p className="text-xs text-gray-400 text-center mt-4">
                      By submitting this form, you&apos;ll hear back from our team within 24 hours.
                    </p>
                  </form>
                </div>
              </motion.div>
            </div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}